import { MUICheckBox, MUITextField } from "@abb/arcadia-ui-react"
import { zodResolver } from "@hookform/resolvers/zod"
import { StatusCodes } from "http-status-codes"
import { MouseEvent, useCallback, useEffect } from "react"
import { Controller, ControllerRenderProps, useForm } from "react-hook-form"
import { useNavigate } from "react-router-dom"
import { toast } from "react-toastify"
import { z } from "zod"
import { digitalManagerApi } from "../../../../services/api"
import { FieldRow, FormPage } from "../../../components/FormPage"
import { DeviceFamilyPost } from "../../../models/DeviceFamily"
import { ControllerRendererProps } from "../../../types/reactHookForm"

const validationSchema = z.object({
  name: z.string().min(1, "Name is required"),
  description: z.string().optional(),
  isRequestable: z.boolean({ error: "Is Requestable is required" }),
})

const returnURL = `/devices#families`

export function FamilyCreate() {
  const navigate = useNavigate()

  const {
    control,
    handleSubmit,
    reset,
    formState: { isSubmitting, isDirty, isValid },
  } = useForm<DeviceFamilyPost>({
    resolver: zodResolver(validationSchema),
  })

  useEffect(() => {
    reset({
      name: "",
      description: "",
      isRequestable: false,
    })
  }, [reset])

  const onSubmit = (values: DeviceFamilyPost) => digitalManagerApi
      .post(`/deviceFamilies`, {
        name: values.name,
        description: values.description,
        isRequestable: values.isRequestable,
      })
      .then((res) => {
        if (res.status === StatusCodes.CREATED) {
          toast.success("Device Family created")
          navigate(returnURL)
        }
      })
      .catch(() => {
        toast.error("Cannot create device family")
      })

  const handleTextFieldChange = useCallback(
    (field: ControllerRenderProps<DeviceFamilyPost, "name" | "description">) =>
      (value: string) =>
        field.onChange(value),
    []
  )

  const handleCheckboxToggle = useCallback(
    (field: ControllerRenderProps<DeviceFamilyPost, "isRequestable">) =>
      (e: MouseEvent<HTMLElement>) => {
        e.stopPropagation()
        field.onChange(!field.value)
      },
    []
  )

  const renderNameTextField = useCallback(
    ({
      field,
      fieldState,
    }: ControllerRendererProps<DeviceFamilyPost, "name">) => (
      <MUITextField
        id="name"
        type="text"
        size={32}
        name={field.name}
        value={field.value}
        onChange={handleTextFieldChange(field)}
        onBlur={field.onBlur}
        isDisabled={field.disabled}
        errors={
          typeof fieldState.error?.message === "string"
            ? { name: { message: fieldState.error.message } }
            : undefined
        }
      />
    ),
    [handleTextFieldChange]
  )

  const renderDescriptionTextField = useCallback(
    ({
      field,
      fieldState,
    }: ControllerRendererProps<DeviceFamilyPost, "description">) => (
      <MUITextField
        id="description"
        type="text"
        size={32}
        name={field.name}
        value={field.value}
        onChange={handleTextFieldChange(field)}
        onBlur={field.onBlur}
        isDisabled={field.disabled}
        errors={
          typeof fieldState.error?.message === "string"
            ? { description: { message: fieldState.error.message } }
            : undefined
        }
      />
    ),
    [handleTextFieldChange]
  )

  const renderCheckbox = useCallback(
    ({ field }: ControllerRendererProps<DeviceFamilyPost, "isRequestable">) => (
      <MUICheckBox
        id="isRequestable"
        checked={!!field.value}
        handleToggle={handleCheckboxToggle(field)}
      />
    ),
    [handleCheckboxToggle]
  )

  return (
    <FormPage
      returnURL={returnURL}
      onSubmit={handleSubmit(onSubmit)}
      isSubmitting={isSubmitting}
      isDirty={isDirty}
      isValid={isValid}
    >
      <FieldRow label="Name">
        <Controller name="name" control={control} render={renderNameTextField} />
      </FieldRow>
      <FieldRow label="Description">
        <Controller
          name="description"
          control={control}
          render={renderDescriptionTextField}
        />
      </FieldRow>
      <FieldRow label="Is Requestable">
        <Controller name="isRequestable" control={control} render={renderCheckbox} />
      </FieldRow>
    </FormPage>
  )
}
